import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { SUPPORTER_VOICE_ENTRY_PARAM } from '#src/app/shared/constants';
import { useClearedQueryParamValue } from '#src/app/shared/hooks/location';
import { isError, isLoaded } from '#src/app/shared/utils/async';
import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isCommentAfterVideoEntry } from '#src/app/shared/utils/supporterVoicesFlow';

import type { PetitionActionsPageCombinedState, PetitionActionsPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PetitionActionsPageDataContextProvider } from './pageData';
import { PetitionActionsPageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<PetitionActionsPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PetitionActionsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const PetitionActionsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);
	// Returning from the video confirmation on the comment-after-video path. Read before the early
	// returns below so it is captured even while the page is still loading, and cleared from the URL as
	// it is read so a reload cannot re-enter a step the supporter has already left.
	const entry = useClearedQueryParamValue(SUPPORTER_VOICE_ENTRY_PARAM);
	const isReturningFromVideo = isCommentAfterVideoEntry(entry);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<PetitionActionsPageDataContextProvider pageData={pageData}>
			<PetitionActionsPageStateContextProvider
				isLastSupporterVoicesPage
				isLoadingMoreSupporterVoices={false}
				// Normally the page opens on the video ask. Arriving back from the video confirmation, the
				// video is already done and the comment is what is left, so it opens on the comment form
				// instead.
				shouldDisplaySupporterVoicesVideo={!isReturningFromVideo}
			>
				<TrackingContext properties={{ petition_id: pageData.data.petition.id }}>{children}</TrackingContext>
			</PetitionActionsPageStateContextProvider>
		</PetitionActionsPageDataContextProvider>
	);
};
